import React from 'react';
import { Modal } from 'reactstrap';
import Button, { ButtonVariant } from './Button';

export interface ConfirmOptions {
  title: React.ReactNode;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' for destructive actions (red confirm button). */
  tone?: 'primary' | 'danger';
  /**
   * Nothing to decide — just something the person has to see. Drops the
   * cancel button, because offering a choice where there isn't one makes
   * people hunt for the difference between the two.
   */
  acknowledge?: boolean;
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

/**
 * Promise-based replacement for window.confirm:
 *   const { confirm, dialog } = useConfirm();
 *   if (!(await confirm({ title: 'Approve refund?' }))) return;
 *   ...render {dialog} once in the component.
 */
export const useConfirm = () => {
  const [pending, setPending] = React.useState<PendingConfirm | null>(null);

  const confirm = React.useCallback(
    (options: ConfirmOptions) => new Promise<boolean>((resolve) => setPending({ ...options, resolve })),
    [],
  );

  const close = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };

  const confirmVariant: ButtonVariant = pending?.tone === 'danger' ? 'danger' : 'primary';

  const dialog = (
    <Modal isOpen={!!pending} toggle={() => close(false)} centered className="ds-dialog" returnFocusAfterClose>
      {pending && (
        <>
          <h2 className="ds-dialog__title">{pending.title}</h2>
          {pending.message && <div className="ds-dialog__body">{pending.message}</div>}
          <div className="ds-dialog__actions">
            {!pending.acknowledge && (
              <Button variant="secondary" onClick={() => close(false)}>
                {pending.cancelLabel ?? 'Cancel'}
              </Button>
            )}
            <Button variant={confirmVariant} onClick={() => close(true)} autoFocus>
              {pending.confirmLabel ?? (pending.acknowledge ? 'OK' : 'Confirm')}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );

  return { confirm, dialog };
};
